import { Dispatch, SetStateAction } from "react";
import {
  API_URL,
  v,
  put,
  debug,
  request,
  takeEvery,
  createDoRequestAction,
  assertTotalSchema,
  SagaIterator,
  call,
  RequestResult,
} from "../../lib";

import {
  doREQUEST_ERROR,
  doREQUEST_COMPLETE,
  doMETHOD_ADD_TASK,
  doTEMP_TASK_DATA_REPLACE_TEMP_TASK,
  doMETHOD_UPDATE_QUESTIONNAIRE,
} from "../state";
import { getToken } from "./token_fetcher";
import {
  confirmQuestionnaireDraftSaveStage,
  failQuestionnaireDraftSave,
  fetchSavedQuestionnaireTasks,
  questionnaireDraftSaveKey,
  questionnaireDraftSaveOwnership,
  SavedQuestionnaireTasksOutcome,
} from "../../lib/questionnaire_draft/coordinator";
import {
  QuestionnaireCreateConflict,
  questionnaireCreateConflictOf,
  showQuestionnaireCreateConflict,
} from "../../lib/questionnaire_draft/create_conflict";
import { currentSessionScope } from "../../lib/auth/session_scope";
import {
  questionnaireDraftCheckpointOf,
  questionnaireDraftSaveIsAmbiguous,
} from "./questionnaire_draft_save";
import {
  createDisplayStrategyUpsertListMap,
  parseDisplayStrategyUpsertListFromMap,
  QuestionnaireDraftKey,
  QuestionnaireDraftSaveStage,
  QuestionnaireTempSection,
  sanitizeTempSection,
  savedQuestionnaireSummaryOf,
  TempQuestionnaireDisplayStrategyUpsertItem,
} from "../../model";

export const REQUEST_TASK_QUESTIONNAIRE_WITH_DISPLAY_STRATEGY_CREATE =
  "REQUEST_TASK_QUESTIONNAIRE_WITH_DISPLAY_STRATEGY_CREATE";
export const doREQUEST_TASK_QUESTIONNAIRE_WITH_DISPLAY_STRATEGY_CREATE = createDoRequestAction(
  REQUEST_TASK_QUESTIONNAIRE_WITH_DISPLAY_STRATEGY_CREATE,
);

export const requestTaskQuestionnaireWithDisplayStrategyCreateActionCreators = {
  doREQUEST_TASK_QUESTIONNAIRE_WITH_DISPLAY_STRATEGY_CREATE,
};

interface Payload {
  payload: {
    requestId: string;
    name: string;
    description?: string;
    methodId: string;
    scheduleRepeat: boolean;
    scheduleDescription: string;
    reminderList?: Record<string, string>[];
    color: string;
    tempTaskId?: string;
    sectionList: QuestionnaireTempSection[];
    displayStrategyList?: TempQuestionnaireDisplayStrategyUpsertItem[];
    // the server refuses a second create with the same key, naming the task the first one made
    createKey?: string;
    // the recovery copy this save finishes; never sent to the server
    draftSave?: { ticketId: string };
  };
  setRequestResult?: Dispatch<SetStateAction<RequestResult | null>>;
}

export function* requestTaskQuestionnaireWithDisplayStrategyCreate({
  payload,
  setRequestResult,
}: Payload): SagaIterator {
  debug("saga*requestTaskQuestionnaireWithDisplayStrategyCreate");

  try {
    assertTotalSchema({
      payload,
      schema: v.object({
        requestId: v.string().uuid().exist(),
        methodId: v.string().uuid().exist(),
        name: v.string().exist(),
        description: v.string().optional(),
        scheduleRepeat: v.boolean().optional(),
        scheduleDescription: v.string().optional(),
        reminderList: v.array().optional(),
        sectionList: v.array(),
        displayStrategyList: v.array().optional(),
        color: v.string().exist(),
        tempTaskId: v.string().uuid().optional(),
        createKey: v.string().uuid().optional(),
        draftSave: v.object({ ticketId: v.string().exist() }).optional(),
      }),
    });
  } catch (error) {
    return yield put(
      doREQUEST_ERROR(
        {
          fromType: REQUEST_TASK_QUESTIONNAIRE_WITH_DISPLAY_STRATEGY_CREATE,
          requestId: payload.requestId,
          error,
        },
        setRequestResult,
      ),
    );
  }

  const {
    name,
    description,
    methodId,
    scheduleRepeat,
    scheduleDescription,
    reminderList,
    sectionList,
    displayStrategyList,
    color,
    tempTaskId,
    createKey,
    draftSave,
  } = payload;

  // A save that finishes a recovery copy belongs to the session it was made in: past a sign-out or a
  // sign-in as someone else -- a replay after a refresh included -- it is neither sent nor published.
  const ticketId = draftSave?.ticketId;
  const ownsSave = questionnaireDraftSaveOwnership(ticketId);
  if (!ownsSave()) return;

  const accessToken = yield call(getToken);
  if (!ownsSave()) return;

  const createTaskResult = yield request({
    method: "post",
    url: `${API_URL}/v1/web/task-questionnaire-create`,
    accessToken,
    data: {
      name,
      description: description || "",
      color,
      methodId,
      scheduleRepeat,
      scheduleDescription,
      reminderList,
      sectionList: sectionList.map(sanitizeTempSection),
      ...(createKey ? { createKey } : {}),
    },
  });

  if (!ownsSave()) return;

  if (!createTaskResult.success || createTaskResult.error) {
    const error = createTaskResult.error || new Error("request did not succeed");
    debug(error);
    const conflict = questionnaireCreateConflictOf(createTaskResult);
    const copyKey = ticketId ? questionnaireDraftSaveKey(ticketId) : null;
    // a 401 is replayed after the refresh, under the same ticket
    if (ticketId && createTaskResult.statusCode !== 401) {
      // A conflict says the copy's create went through, or is going through, under its key: like a
      // create whose answer was lost, the copy keeps its submitted mark and is not deleted
      failQuestionnaireDraftSave(ticketId, {
        ambiguous: conflict !== null || questionnaireDraftSaveIsAmbiguous(createTaskResult),
      });
    }
    yield put(
      doREQUEST_ERROR(
        {
          fromType: REQUEST_TASK_QUESTIONNAIRE_WITH_DISPLAY_STRATEGY_CREATE,
          fromPayload: payload,
          requestId: payload.requestId,
          url: "/v1/web/task-questionnaire-create",
          ...createTaskResult,
          error,
        },
        setRequestResult,
      ),
    );
    if (conflict) yield call(reportQuestionnaireCreateConflict, { conflict, methodId, copyKey });
    return;
  }

  if (tempTaskId) {
    yield put(
      doTEMP_TASK_DATA_REPLACE_TEMP_TASK({
        tempTaskId,
        newId: createTaskResult.payload.id,
      }),
    );
  } else {
    yield put(doMETHOD_ADD_TASK({ ...createTaskResult.payload }));
  }
  if (ticketId) {
    confirmQuestionnaireDraftSaveStage(ticketId, QuestionnaireDraftSaveStage.Content, {
      checkpoint: questionnaireDraftCheckpointOf(createTaskResult.payload),
    });
  }

  // create display strategy if provided
  let displayStrategyResult = {};
  if (displayStrategyList?.length) {
    if (!ownsSave()) return;
    const displayStrategyUpsertItemMapList = createDisplayStrategyUpsertListMap(
      sectionList, //tempQuestionnaireSectionList
      displayStrategyList,
    );

    const newQuestionnaire = createTaskResult.payload.questionnaire;
    const upsertDisplayStrategyResult = yield request({
      method: "post",
      url: `${API_URL}/v1/web/questionnaire-display-strategy-upsert`,
      accessToken,
      data: {
        questionnaireId: newQuestionnaire.id,
        displayStrategyList: parseDisplayStrategyUpsertListFromMap(
          newQuestionnaire.sectionList,
          displayStrategyUpsertItemMapList,
        ),
      },
    });

    if (!ownsSave()) return;

    if (!upsertDisplayStrategyResult.success || upsertDisplayStrategyResult.error) {
      const error = upsertDisplayStrategyResult.error || new Error("request did not succeed");
      if (ticketId) {
        failQuestionnaireDraftSave(ticketId, {
          ambiguous: questionnaireDraftSaveIsAmbiguous(upsertDisplayStrategyResult),
        });
      }
      return yield put(
        doREQUEST_ERROR(
          {
            fromType: REQUEST_TASK_QUESTIONNAIRE_WITH_DISPLAY_STRATEGY_CREATE,
            requestId: payload.requestId,
            error,
          },
          setRequestResult,
        ),
      );
    }

    yield put(
      doMETHOD_UPDATE_QUESTIONNAIRE({
        ...upsertDisplayStrategyResult.payload,
      }),
    );
    displayStrategyResult = { ...upsertDisplayStrategyResult.payload };
  }
  if (ticketId)
    confirmQuestionnaireDraftSaveStage(ticketId, QuestionnaireDraftSaveStage.DisplayStrategy);

  yield put(
    doREQUEST_COMPLETE(
      {
        fromType: REQUEST_TASK_QUESTIONNAIRE_WITH_DISPLAY_STRATEGY_CREATE,
        requestId: payload.requestId,
        ...createTaskResult,
        upsertDisplayStrategyResult: displayStrategyResult,
      },
      setRequestResult,
    ),
  );
}

// Tells the researcher why nothing was created. A create that went through already is shown as the
// saved questionnaire, with the method's tasks fetched again so it is in the list.
function* reportQuestionnaireCreateConflict({
  conflict,
  methodId,
  copyKey,
}: {
  conflict: QuestionnaireCreateConflict;
  methodId: string;
  copyKey: QuestionnaireDraftKey | null;
}): SagaIterator {
  const scope = currentSessionScope();
  if (!scope) return;
  if (conflict.kind === "in_progress") {
    showQuestionnaireCreateConflict({ kind: "in_progress", scope });
    return;
  }
  const saved: SavedQuestionnaireTasksOutcome = yield call(fetchSavedQuestionnaireTasks, methodId);
  const savedTask =
    saved.kind === "ok" ? saved.taskList.find(({ id }) => id === conflict.taskId) : undefined;
  showQuestionnaireCreateConflict({
    kind: "already_created",
    scope,
    copyKey,
    savedTask: savedTask ? savedQuestionnaireSummaryOf(savedTask) : null,
  });
}

export function* taskQuestionnaireWithDisplayStrategyCreateSaga() {
  yield takeEvery(
    REQUEST_TASK_QUESTIONNAIRE_WITH_DISPLAY_STRATEGY_CREATE as any,
    requestTaskQuestionnaireWithDisplayStrategyCreate,
  );
}
